import { type FC, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '@/store/hooks';
import { selectIncidentGatheringId, selectActiveCountyId } from '@/store/auth';
import { useGetTeamsQuery } from '@/services/teamsApi';
import { useDebounce } from '@/hooks/useDebounce';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import type { TeamResponse } from '@/common/types/teams.types';
import TeamsView from './Teams.view';
import type { TeamsProps } from './Teams.props';

const PAGE_SIZE = 20;

const TeamsContainer: FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const incidentGatheringId = useAppSelector(selectIncidentGatheringId);
  const groupId = useAppSelector(selectActiveCountyId);

  // Local state for pagination
  const [offset, setOffset] = useState(0);

  // Debounce search for API calls
  const debouncedSearch = useDebounce(searchTerm, 300);

  // Track previous search and pages for accumulation
  const prevSearchRef = useRef(debouncedSearch);
  const pagesRef = useRef<Map<number, TeamResponse[]>>(new Map());
  const [, forceUpdate] = useState(0);

  // Fetch teams from API with search and pagination params
  // Polling every 60 seconds to keep data fresh
  const { data: teamsData, isLoading, isFetching } = useGetTeamsQuery(
    {
      incidentId: incidentGatheringId || undefined,
      search: debouncedSearch || undefined,
      groupId: groupId || undefined,
      limit: PAGE_SIZE,
      offset,
    },
    { skip: !incidentGatheringId, pollingInterval: 60000 }
  );

  // Handle search change - reset pagination (in render, not effect)
  if (prevSearchRef.current !== debouncedSearch) {
    prevSearchRef.current = debouncedSearch;
    pagesRef.current.clear();
    if (offset !== 0) {
      setOffset(0);
    }
  }

  // Store current page data in ref (in render, not effect)
  if (teamsData?.dataset) {
    const currentPage = pagesRef.current.get(offset);
    if (currentPage !== teamsData.dataset) {
      pagesRef.current.set(offset, teamsData.dataset);
      // Force re-render to update derived accumulatedItems
      forceUpdate((n) => n + 1);
    }
  }

  // Derive accumulated items from pages ref
  const accumulatedTeams = useMemo(() => {
    const items: TeamResponse[] = [];
    const seenIds = new Set<string>();

    // Sort pages by offset and flatten
    const sortedOffsets = Array.from(pagesRef.current.keys()).sort((a, b) => a - b);

    for (const off of sortedOffsets) {
      const pageItems = pagesRef.current.get(off) || [];
      for (const item of pageItems) {
        if (!seenIds.has(item.id)) {
          seenIds.add(item.id);
          items.push(item);
        }
      }
    }
    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamsData, debouncedSearch]);

  // Calculate if there are more items to load
  const totalCount = teamsData?.count ?? 0;
  const hasMore = accumulatedTeams.length < totalCount;

  // Load more callback for infinite scroll
  const handleLoadMore = useCallback(() => {
    if (!isFetching && hasMore) {
      setOffset((prev) => prev + PAGE_SIZE);
    }
  }, [isFetching, hasMore]);

  // Infinite scroll hook
  const { sentinelRef } = useInfiniteScroll({
    onLoadMore: handleLoadMore,
    disabled: !hasMore,
    isLoading: isFetching,
    threshold: '100px',
  });

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleAddClick = () => {
    navigate('new');
  };

  const handleRowClick = (teamId: string) => {
    navigate(teamId);
  };

  // Determine loading states
  const isInitialLoading = isLoading && accumulatedTeams.length === 0;
  const isLoadingMore = isFetching && accumulatedTeams.length > 0;
  const isSearching =
    searchTerm !== debouncedSearch || (isFetching && offset === 0 && searchTerm !== '');

  const combinedProps: TeamsProps = {
    count: totalCount,
    teams: accumulatedTeams,
    isLoading: isInitialLoading,
    isLoadingMore,
    isSearching,
    hasMore,
    handleAddClick,
    handleRowClick,
    handleSearchChange,
    searchTerm,
    sentinelRef,
  };

  return <TeamsView {...combinedProps} />;
};

export default TeamsContainer;
